import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import useNoteStore from '../../store/noteStore';
import useCalendarStore from '../../store/calendarStore';
import { EventType, EVENT_TYPE_CONFIGS } from '../../types/eventTypes';
import { RecurringConfig } from '../../types/recurringTypes';
import { GioChecklistItem, DEFAULT_GIO_CHECKLIST, calculateGioYearNumber, formatGioYearText, isGioEvent } from '../../types/gioTypes';
import { Reminder, createDefaultReminders } from '../../types/reminderTypes';
import EventTypeSelector from './EventTypeSelector';
import DynamicEventFields from './DynamicEventFields';
import RecurringToggle from './RecurringToggle';
import GioChecklist from './GioChecklist';
import ReminderSelector from './ReminderSelector';

const NoteEditor: React.FC = () => {
    const { currentDate } = useCalendarStore();
    const { isEditorOpen, editingNote, closeEditor, createNote, updateNote } = useNoteStore();

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [tags, setTags] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Event Type fields
    const [eventType, setEventType] = useState<EventType>(EventType.KHAC);
    const [eventData, setEventData] = useState<Record<string, unknown>>({});
    // Recurring config (Feature A2)
    const [recurring, setRecurring] = useState<RecurringConfig | undefined>(undefined);
    // Giỗ checklist (Feature A3)
    const [gioChecklist, setGioChecklist] = useState<GioChecklistItem[]>(DEFAULT_GIO_CHECKLIST);
    // Reminders (Feature A5)
    const [reminders, setReminders] = useState<Reminder[]>([]);

    useEffect(() => {
        if (editingNote) {
            setTitle(editingNote.title);
            setContent(editingNote.content);
            setTags(editingNote.tags?.join(', ') || '');
            setEventType(editingNote.eventType || EventType.KHAC);
            setEventData(editingNote.eventData || {});
            setRecurring(editingNote.recurring);
            // Restore checklist from eventData if available
            if (editingNote.eventData?.checklist) {
                setGioChecklist(editingNote.eventData.checklist as GioChecklistItem[]);
            } else {
                setGioChecklist(DEFAULT_GIO_CHECKLIST);
            }
            // Restore reminders
            setReminders(editingNote.reminders || []);
        } else {
            setTitle('');
            setContent('');
            setTags('');
            setEventType(EventType.KHAC);
            setEventData({});
            setRecurring(undefined);
            setGioChecklist(DEFAULT_GIO_CHECKLIST);
            setReminders([]);
        }
    }, [editingNote, isEditorOpen]);

    const handleEventTypeChange = (type: EventType) => {
        setEventType(type);
        setEventData({}); // Reset data when type changes
        // Reset checklist when switching to/from Giỗ
        if (type === EventType.GIO) {
            setGioChecklist(DEFAULT_GIO_CHECKLIST);
        }
        // Set default reminders for new event type
        const defaultDays = EVENT_TYPE_CONFIGS[type]?.defaultReminders || [];
        setReminders(createDefaultReminders(defaultDays));
    };

    // Calculate năm thứ X for preview
    const gioYearNumber = isGioEvent(eventType)
        ? calculateGioYearNumber(eventData.deathYear as number | string | undefined)
        : null;

    const handleSave = async () => {
        if (!title.trim() && !content.trim() && Object.keys(eventData).length === 0) {
            closeEditor();
            return;
        }

        setIsSaving(true);
        const tagArray = tags.split(',').map((t) => t.trim()).filter(Boolean);
        const dateStr = format(currentDate, 'yyyy-MM-dd');

        try {
            if (editingNote) {
                await updateNote(editingNote.id || editingNote.localId || '', {
                    title: title.trim(),
                    content: content.trim(),
                    tags: tagArray,
                    eventType,
                    eventData: isGioEvent(eventType)
                        ? { ...eventData, checklist: gioChecklist }
                        : eventData,
                    recurring,
                    reminders,
                });
            } else {
                await createNote({
                    date: dateStr,
                    title: title.trim(),
                    content: content.trim(),
                    tags: tagArray,
                    eventType,
                    eventData: isGioEvent(eventType)
                        ? { ...eventData, checklist: gioChecklist }
                        : eventData,
                    recurring,
                    reminders,
                });
            }
            closeEditor();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <AnimatePresence>
            {isEditorOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeEditor}
                        className="fixed inset-0 bg-black/50 z-40"
                    />

                    {/* Editor Modal - Full screen on mobile, centered modal on desktop */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed inset-0 md:inset-auto md:bottom-0 md:left-1/2 md:-translate-x-1/2 md:max-w-2xl md:w-full bg-slate-800 md:rounded-t-2xl z-50 flex flex-col max-h-[90vh]"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-white/10 flex-shrink-0">
                            <button
                                onClick={closeEditor}
                                className="text-white/70 hover:text-white px-3 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-base sm:text-lg"
                            >
                                Hủy
                            </button>
                            <h3 className="text-white font-medium text-base sm:text-lg">
                                {editingNote ? 'Sửa sự kiện' : 'Sự kiện mới'}
                            </h3>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="text-blue-400 hover:text-blue-300 px-3 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center font-medium disabled:opacity-50 text-base sm:text-lg"
                            >
                                {isSaving ? 'Đang lưu...' : 'Lưu'}
                            </button>
                        </div>

                        {/* Form - scrollable on mobile */}
                        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
                            {/* Event Type Selector */}
                            <EventTypeSelector
                                selected={eventType}
                                onSelect={handleEventTypeChange}
                            />

                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Tiêu đề
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Nhập tiêu đề..."
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 text-base"
                                    autoFocus
                                />
                            </div>

                            {/* Dynamic Fields Based on Event Type */}
                            <DynamicEventFields
                                eventType={eventType}
                                eventData={eventData}
                                onChange={setEventData}
                            />

                            {/* Recurring Toggle (Feature A2) */}
                            <RecurringToggle
                                config={recurring}
                                eventType={eventType}
                                onChange={setRecurring}
                            />

                            {/* Reminders (Feature A5) */}
                            <ReminderSelector
                                reminders={reminders}
                                onChange={setReminders}
                                eventType={eventType}
                                eventDate={currentDate}
                            />

                            {/* Giỗ-specific: Năm thứ X Preview (Feature A3) */}
                            {isGioEvent(eventType) && gioYearNumber && (
                                <div className="flex items-center gap-2 p-3 bg-amber-500/20 rounded-lg border border-amber-500/30">
                                    <span className="text-amber-400 text-xl">🕯️</span>
                                    <span className="text-amber-300 font-medium">
                                        {formatGioYearText(gioYearNumber)}
                                    </span>
                                    <span className="text-white/60 text-sm">
                                        (từ năm {String(eventData.deathYear)})
                                    </span>
                                </div>
                            )}

                            {/* Giỗ-specific: Preparation Checklist (Feature A3) */}
                            {isGioEvent(eventType) && (
                                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                                    <GioChecklist
                                        items={gioChecklist}
                                        onChange={setGioChecklist}
                                    />
                                </div>
                            )}

                            {/* Additional Notes */}
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Ghi chú thêm
                                </label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="Thêm ghi chú..."
                                    rows={4}
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 resize-none text-base"
                                />
                            </div>

                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-medium text-white/70 mb-2">
                                    Tags
                                </label>
                                <input
                                    type="text"
                                    value={tags}
                                    onChange={(e) => setTags(e.target.value)}
                                    placeholder="Phân cách bằng dấu phẩy"
                                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-400 text-base"
                                />
                            </div>

                            <p className="text-white/40 text-xs">
                                Ngày: {format(currentDate, 'dd/MM/yyyy')}
                            </p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default NoteEditor;
